import { mobileDashboardHeaderSx, mobileDashboardActionsSx } from "./dashboardMobileLayout";
import {
  ArrowForwardOutlined,
  InventoryOutlined,
  LocalShippingOutlined,
  RefreshOutlined,
  TrendingDownOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { decideReplenishmentProposal, loadReplenishmentProposal, loadStockRisk, MaterialStockPolicyEvaluation,
  ReplenishmentProposal, startReplenishmentProposalReview } from "./api/product";
import { HomeDashboardSelector } from "./HomeDashboardSelector";

interface StockoutRiskDashboardProps {
  canRead: boolean;
  canReviewMaterials?: boolean;
  canApproveProposals?: boolean;
  onShowOperations: () => void;
  onShowReplenishment?: () => void;
  onShowStockRiskV2?: () => void;
  onReviewMaterials: (itemId: string) => void;
  operationsLabel?: string;
}

const severityOrder: Record<MaterialStockPolicyEvaluation["evaluationStatus"], number> = {
  CRITICAL: 0, REPLENISH: 1, WATCH: 2, HEALTHY: 3, INACTIVE: 4
};

function quantity(value: number, uom: string) {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)} ${uom}`;
}

function RiskChip({ status }: { status: MaterialStockPolicyEvaluation["evaluationStatus"] }) {
  const { t } = useTranslation();
  const color = status === "CRITICAL" ? "error"
    : status === "REPLENISH" ? "warning"
      : status === "WATCH" ? "info" : status === "HEALTHY" ? "success" : "default";
  return <Chip size="small" color={color} label={t(`dashboard.stockRisk.status.${status}`)}
    sx={{ minWidth: 86, fontWeight: 800 }} />;
}

export function StockoutRiskDashboard({
  canRead, canReviewMaterials = true, canApproveProposals = false,
  onShowOperations, onShowReplenishment, onShowStockRiskV2, onReviewMaterials,
  operationsLabel
}: StockoutRiskDashboardProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [proposal, setProposal] = useState<ReplenishmentProposal | null>(null);
  const [decisionQuantity, setDecisionQuantity] = useState("");
  const [decisionReason, setDecisionReason] = useState("");
  const risk = useQuery({
    queryKey: ["material-stock-risk"], queryFn: loadStockRisk, enabled: canRead,
    refetchInterval: 60_000
  });
  const updatedTime = new Date(risk.dataUpdatedAt || Date.now()).toLocaleTimeString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR", {
      hour: "2-digit", minute: "2-digit"
    });
  const rows = [...(risk.data ?? [])].sort((a, b) =>
    severityOrder[a.evaluationStatus] - severityOrder[b.evaluationStatus]
      || a.policy.itemCode.localeCompare(b.policy.itemCode)
  );
  const actionable = rows.filter((row) =>
    ["CRITICAL", "REPLENISH", "WATCH"].includes(row.evaluationStatus)
  );
  const count = (status: MaterialStockPolicyEvaluation["evaluationStatus"]) =>
    rows.filter((row) => row.evaluationStatus === status).length;
  const inbound = rows.reduce((sum, row) => sum + row.confirmedInboundQuantity, 0);
  const date = (value: string | null) => value
    ? new Date(value).toLocaleDateString(i18n.language.startsWith("en") ? "en-US" : "fr-FR")
    : "—";
  const dateTime = (value: string | null) => value
    ? new Date(value).toLocaleString(i18n.language.startsWith("en") ? "en-US" : "fr-FR")
    : "—";
  const review = useMutation({
    mutationFn: (row: MaterialStockPolicyEvaluation) => row.proposalId
      ? loadReplenishmentProposal(row.proposalId)
      : startReplenishmentProposalReview(row.policy.id),
    onSuccess: (created) => {
      setProposal(created);
      setDecisionQuantity(String(created.recommendedQuantity));
      setDecisionReason("");
    }
  });
  const decision = useMutation({
    mutationFn: ({ decision: nextDecision }: { decision: "APPROVED" | "REJECTED" }) =>
      decideReplenishmentProposal(proposal!.id, {
        quantity: Number(decisionQuantity), decision: nextDecision,
        reason: decisionReason.trim(), version: proposal!.version
      }),
    onSuccess: () => {
      setProposal(null);
      void queryClient.invalidateQueries({ queryKey: ["material-stock-risk"] });
    }
  });

  return <Stack spacing={1.25} sx={{ width: "100%", "@media (max-width: 599.95px)": { "& > *": { flexShrink: 0 } }, height: { xs: "auto", sm: "100%" }, minHeight: 0 }}>
    <Stack sx={mobileDashboardHeaderSx} direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
      <Box>
        <Typography variant="h4" component="h1" fontWeight={850}
          sx={{ fontSize: { xs: "1.4rem", md: "1.75rem" } }}>
          {t("dashboard.stockRisk.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("dashboard.stockRisk.subtitle")}
        </Typography>
      </Box>
      <Stack sx={mobileDashboardActionsSx} direction="row" spacing={1} alignItems="center">
        <HomeDashboardSelector value="stock-risk"
          operationsLabel={operationsLabel}
          showStockRiskV2={Boolean(onShowStockRiskV2)}
          onChange={(view) => view === "operations" ? onShowOperations()
            : view === "stock-risk-v2" ? onShowStockRiskV2?.()
            : view === "replenishment" ? onShowReplenishment?.() : undefined} />
        <IconButton size="small" aria-label={t("dashboard.refresh")}
          onClick={() => void risk.refetch()}><RefreshOutlined /></IconButton>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t("dashboard.updated", { time: updatedTime })}
        </Typography>
      </Stack>
    </Stack>

    {!canRead ? <Alert severity="warning">{t("dashboard.stockRisk.noPermission")}</Alert> : null}
    {risk.isError ? <Alert severity="error">{t("dashboard.stockRisk.loadError")}</Alert> : null}

    <Grid container spacing={1.25}>
      {[
        { key: "critical", value: count("CRITICAL"), color: "error.main", icon: <WarningAmberOutlined /> },
        { key: "replenish", value: count("REPLENISH"), color: "warning.main", icon: <TrendingDownOutlined /> },
        { key: "watch", value: count("WATCH"), color: "info.main", icon: <InventoryOutlined /> },
        { key: "inbound", value: inbound, color: "secondary.main", icon: <LocalShippingOutlined /> }
      ].map(({ key, value, color, icon }) => <Grid key={key} size={{ xs: 6, md: 3 }}>
        <Card variant="outlined" sx={{ px: 1.5, py: 1, borderTop: 3, borderColor: color }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ color, display: "flex" }}>{icon}</Box>
            <Box>
              <Typography variant="h5" fontWeight={850} lineHeight={1}>{Number(value)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t(`dashboard.stockRisk.metrics.${key}`)}
              </Typography>
            </Box>
          </Stack>
        </Card>
      </Grid>)}
    </Grid>

    <Card variant="outlined" sx={{ flex: "1 1 52%", minHeight: 250,
      display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
        sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
        <Typography fontWeight={850}>{t("dashboard.stockRisk.current.title")}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t("dashboard.stockRisk.current.horizon")}
        </Typography>
      </Stack>
      {risk.isLoading ? <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
        : !rows.length ? <Alert severity="info" sx={{ m: 1.5 }}>
          {t("dashboard.stockRisk.current.empty")}
        </Alert> : <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1180 }}>
            <TableHead><TableRow>
              {["material", "status", "physical", "unavailable", "reserved", "available",
                "inbound", "consumption", "forecast", "coverage", "stockout"].map((key) =>
                <TableCell key={key} align={key === "material" || key === "status" ? "left" : "right"}
                  sx={{ py: .75, fontWeight: 800, whiteSpace: "nowrap" }}>
                  {t(`dashboard.stockRisk.columns.${key}`)}
                </TableCell>)}
            </TableRow></TableHead>
            <TableBody>{rows.map((row) => <TableRow key={row.policy.id} hover>
              <TableCell sx={{ py: .65 }}>
                <Typography variant="body2" fontWeight={800}>{row.policy.itemCode}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.policy.itemDisplayName} · {row.policy.warehouseCode}
                </Typography>
              </TableCell>
              <TableCell><RiskChip status={row.evaluationStatus} /></TableCell>
              <TableCell align="right">{quantity(row.onHandQuantity, row.policy.baseUom)}</TableCell>
              <TableCell align="right">{quantity(row.unavailableQuantity, row.policy.baseUom)}</TableCell>
              <TableCell align="right">{quantity(row.reservedQuantity, row.policy.baseUom)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>{quantity(row.availableQuantity, row.policy.baseUom)}</TableCell>
              <TableCell align="right">
                <Typography variant="body2">{quantity(row.confirmedInboundQuantity, row.policy.baseUom)}</Typography>
                <Typography variant="caption" color="text.secondary">{date(row.nextConfirmedInboundAt)}</Typography>
              </TableCell>
              <TableCell align="right">{quantity(row.averageDailyConsumption, `${row.policy.baseUom}/${t("dashboard.stockRisk.day")}`)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>{quantity(row.forecastAvailableQuantity, row.policy.baseUom)}</TableCell>
              <TableCell align="right">{row.coverageDays == null ? "—" : `${row.coverageDays} ${t("dashboard.stockRisk.days")}`}</TableCell>
              <TableCell align="right">{date(row.projectedStockoutDate)}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>}
    </Card>

    <Card variant="outlined" sx={{ flex: "0 1 34%", minHeight: 180,
      display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
        sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
        <Box>
          <Typography fontWeight={850}>{t("dashboard.stockRisk.proposals.title")}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.stockRisk.proposals.subtitle")}
          </Typography>
        </Box>
        <Chip size="small" label={actionable.length} color={actionable.length ? "warning" : "success"} />
      </Stack>
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {!actionable.length ? <Alert severity="success" sx={{ m: 1.5 }}>
          {t("dashboard.stockRisk.proposals.empty")}
        </Alert> : actionable.map((row) => <Stack key={row.policy.id} direction="row"
          alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
          <RiskChip status={row.evaluationStatus} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={800} noWrap>
              {row.policy.itemCode} · {row.policy.itemDisplayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("dashboard.stockRisk.proposals.reason", {
                available: quantity(row.availableQuantity, row.policy.baseUom),
                minimum: quantity(row.policy.minimumQuantity, row.policy.baseUom),
                date: date(row.recommendedLaunchDate)
              })}
            </Typography>
          </Box>
          <Typography fontWeight={850} sx={{ whiteSpace: "nowrap" }}>
            + {quantity(row.recommendedReplenishmentQuantity, row.policy.baseUom)}
          </Typography>
          {(canReviewMaterials || row.proposalId) && <Button size="small" variant="outlined"
            endIcon={<ArrowForwardOutlined />} disabled={review.isPending}
            color={row.proposalStatus === "APPROVED" ? "success"
              : row.proposalStatus === "REJECTED" ? "error" : "primary"}
            onClick={() => review.mutate(row)}>
            {row.proposalStatus
              ? t(`dashboard.stockRisk.review.status.${row.proposalStatus}`)
              : t("dashboard.stockRisk.proposals.review")}
          </Button>}
        </Stack>)}
      </Box>
    </Card>
    {review.isError && <Alert severity="error">
      {t("dashboard.stockRisk.review.loadError")}
    </Alert>}
    <Dialog open={proposal != null} onClose={() => setProposal(null)} fullWidth maxWidth="md">
      {proposal && <>
        <DialogTitle>
          {t("dashboard.stockRisk.review.title")} · {proposal.itemCode}
          <Typography variant="body2" color="text.secondary">
            {proposal.itemDisplayName} · {proposal.warehouseCode}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RiskChip status={proposal.evaluationStatus} />
              <Chip size="small" label={t(`dashboard.stockRisk.review.status.${proposal.status}`)} />
            </Stack>
            <Grid container spacing={1.5}>{[
              ["physical", proposal.onHandQuantity],
              ["unavailable", proposal.unavailableQuantity],
              ["reserved", proposal.reservedQuantity],
              ["available", proposal.availableQuantity],
              ["inbound", proposal.confirmedInboundQuantity],
              ["consumption", proposal.averageDailyConsumption]
            ].map(([key, value]) => <Grid key={String(key)} size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {t(`dashboard.stockRisk.review.fields.${key}`)}
              </Typography>
              <Typography fontWeight={800}>{quantity(Number(value), proposal.baseUom)}</Typography>
            </Grid>)}</Grid>
            <Divider />
            <Typography fontWeight={800}>{t("dashboard.stockRisk.review.ruleTitle")}</Typography>
            <Typography variant="body2">
              {t("dashboard.stockRisk.review.rule", {
                available: quantity(proposal.availableQuantity, proposal.baseUom),
                inbound: quantity(proposal.confirmedInboundQuantity, proposal.baseUom),
                consumption: quantity(proposal.averageDailyConsumption * 30, proposal.baseUom),
                forecast: quantity(proposal.forecastAvailableQuantity, proposal.baseUom)
              })}
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">{t("dashboard.stockRisk.review.fields.safety")}</Typography><Typography fontWeight={800}>{quantity(proposal.safetyStockQuantity, proposal.baseUom)}</Typography></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">{t("dashboard.stockRisk.review.fields.minimum")}</Typography><Typography fontWeight={800}>{quantity(proposal.minimumQuantity, proposal.baseUom)}</Typography></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">{t("dashboard.stockRisk.review.fields.maximum")}</Typography><Typography fontWeight={800}>{quantity(proposal.maximumQuantity, proposal.baseUom)}</Typography></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">{t("dashboard.stockRisk.review.fields.launch")}</Typography><Typography fontWeight={800}>{date(proposal.recommendedLaunchDate)}</Typography></Grid>
            </Grid>
            <Alert severity="warning" icon={<TrendingDownOutlined />}>
              {t("dashboard.stockRisk.review.recommendation", {
                quantity: quantity(proposal.recommendedQuantity, proposal.baseUom)
              })}
            </Alert>
            {proposal.decidedQuantity != null
              && ["APPROVED", "REJECTED", "ORDERED"].includes(proposal.status) &&
              <Alert severity={proposal.status === "REJECTED" ? "error" : "success"}>
                <Typography fontWeight={850}>
                  {t(`dashboard.stockRisk.review.${proposal.status === "REJECTED"
                    ? "rejectedQuantity" : "approvedQuantity"}`, {
                    quantity: quantity(proposal.decidedQuantity, proposal.baseUom)
                  })}
                </Typography>
                <Typography variant="body2">
                  {t("dashboard.stockRisk.review.decidedAt", {
                    date: dateTime(proposal.decidedAt)
                  })}
                </Typography>
                {proposal.decisionReason && <Typography variant="body2">
                  {t("dashboard.stockRisk.review.decisionReason", {
                    reason: proposal.decisionReason
                  })}
                </Typography>}
              </Alert>}
            {canApproveProposals && proposal.status === "UNDER_REVIEW" && <>
              <Divider />
              <Typography fontWeight={800}>{t("dashboard.stockRisk.review.decisionTitle")}</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField type="number" label={t("dashboard.stockRisk.review.fields.decidedQuantity")}
                  value={decisionQuantity} onChange={(event) => setDecisionQuantity(event.target.value)}
                  slotProps={{ htmlInput: { min: 0.000001, step: "any" } }} sx={{ minWidth: 220 }} />
                <TextField fullWidth label={t("dashboard.stockRisk.review.fields.reason")}
                  value={decisionReason} onChange={(event) => setDecisionReason(event.target.value)}
                  helperText={t("dashboard.stockRisk.review.reasonHelp")} />
              </Stack>
              {decision.isError && <Alert severity="error">{t("dashboard.stockRisk.review.decisionError")}</Alert>}
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          {canApproveProposals && proposal.status === "UNDER_REVIEW" && <Button color="error" disabled={decision.isPending
            || !decisionReason.trim() || !(Number(decisionQuantity) > 0)}
            onClick={() => decision.mutate({ decision: "REJECTED" })}>
            {t("dashboard.stockRisk.review.reject")}
          </Button>}
          {canApproveProposals && proposal.status === "UNDER_REVIEW" && <Button color="success" variant="contained"
            disabled={decision.isPending || !(Number(decisionQuantity) > 0)}
            onClick={() => decision.mutate({ decision: "APPROVED" })}>
            {t("dashboard.stockRisk.review.approve")}
          </Button>}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setProposal(null)}>{t("common.actions.close")}</Button>
          <Button variant="contained" onClick={() => {
            const itemId = proposal.itemId;
            setProposal(null);
            onReviewMaterials(itemId);
          }}>
            {t("dashboard.stockRisk.review.openMaterial")}
          </Button></DialogActions>
      </>}
    </Dialog>
  </Stack>;
}
